/*----------------------------------
- WEBSITE CRAWLER POLICY
----------------------------------*/

/**
 * @fix  docs/fixes/2026-07-18-website-legacy-marketing-redirects.md
 * @rule Do not replace the exact-root resolver with `startsWith`, a broad regular expression, or parsed
 *       query reconstruction. Do not add the private crawler header to these three public redirects. Do
 *       not remove the literal slash-plus-query robots allows while the nested disallows remain. Do not
 *       change `/projects` away from `/solutions/founders` without a new product decision.
 */

import { isWebsitePublicRedirectAliasPath } from '@/common/catalogs/publicRedirects';

export type CrawlerGroup = 'search_ai' | 'user_fetch' | 'search_traditional' | 'training' | 'unknown' | 'human';

export type CrawlerBotType =
    | 'OAI-SearchBot'
    | 'ChatGPT-User'
    | 'PerplexityBot'
    | 'Perplexity-User'
    | 'Claude-SearchBot'
    | 'Claude-User'
    | 'Googlebot'
    | 'bingbot'
    | 'GPTBot'
    | 'ClaudeBot'
    | 'Google-Extended'
    | 'unknown'
    | 'human';

export type CrawlerClassification = {
    botType: CrawlerBotType;
    crawlerGroup: CrawlerGroup;
};

type CrawlerMatcher = {
    botType: Exclude<CrawlerBotType, 'unknown' | 'human'>;
    crawlerGroup: Exclude<CrawlerGroup, 'unknown' | 'human'>;
    tokens: readonly string[];
};

export const PUBLIC_INDEX_ROBOTS_DIRECTIVES = 'index,follow,max-image-preview:large';
export const FACETED_SEARCH_ROBOTS_DIRECTIVES = 'noindex,follow,max-image-preview:large';
/** A page that must exist and stay linked, but must not be indexed yet. */
export const UNPUBLISHED_PAGE_ROBOTS_DIRECTIVES = 'noindex,nofollow';
export const API_X_ROBOTS_TAG = 'noindex, nofollow';
export const PRIVATE_X_ROBOTS_TAG = 'noindex, nofollow, noarchive';

export const WEBSITE_PRIVATE_CRAWLER_PATHS = [
    '/api/',
    '/user/',
    '/checkout/',
    '/admin/',
    '/convert/',
    '/stripe/',
    '/webhooks/',
    '/dashboard/',
    '/radars/',
    '/watchlists/',
    '/vault/',
    '/projects/',
    '/settings/',
    '/download/',
] as const;

const CRAWLER_MATCHERS: readonly CrawlerMatcher[] = [
    { botType: 'OAI-SearchBot', crawlerGroup: 'search_ai', tokens: ['oai-searchbot'] },
    { botType: 'ChatGPT-User', crawlerGroup: 'user_fetch', tokens: ['chatgpt-user'] },
    { botType: 'Perplexity-User', crawlerGroup: 'user_fetch', tokens: ['perplexity-user'] },
    { botType: 'PerplexityBot', crawlerGroup: 'search_ai', tokens: ['perplexitybot'] },
    { botType: 'Claude-SearchBot', crawlerGroup: 'search_ai', tokens: ['claude-searchbot'] },
    { botType: 'Claude-User', crawlerGroup: 'user_fetch', tokens: ['claude-user'] },
    { botType: 'Googlebot', crawlerGroup: 'search_traditional', tokens: ['googlebot'] },
    { botType: 'bingbot', crawlerGroup: 'search_traditional', tokens: ['bingbot'] },
    { botType: 'GPTBot', crawlerGroup: 'training', tokens: ['gptbot'] },
    { botType: 'ClaudeBot', crawlerGroup: 'training', tokens: ['claudebot'] },
    { botType: 'Google-Extended', crawlerGroup: 'training', tokens: ['google-extended'] },
] as const;

export const normalizeCrawlerPathname = (pathname: string): string => {
    if (!pathname || pathname === '/') return '/';

    return pathname.replace(/\/+$/, '') || '/';
};

export const classifyCrawlerUserAgent = (userAgent: string | null | undefined): CrawlerClassification => {
    const normalized = (userAgent || '').toLowerCase();
    if (!normalized) {
        return {
            botType: 'unknown',
            crawlerGroup: 'unknown',
        };
    }

    const matchedCrawler = CRAWLER_MATCHERS.find((matcher) =>
        matcher.tokens.some((token) => normalized.includes(token)),
    );

    if (matchedCrawler) {
        return {
            botType: matchedCrawler.botType,
            crawlerGroup: matchedCrawler.crawlerGroup,
        };
    }

    if (normalized.includes('bot') || normalized.includes('crawler') || normalized.includes('spider')) {
        return {
            botType: 'unknown',
            crawlerGroup: 'unknown',
        };
    }

    return {
        botType: 'human',
        crawlerGroup: 'human',
    };
};

export const isPrivateCrawlerPath = (pathname: string): boolean => {
    const rawPathname = pathname || '/';
    const normalizedPathname = normalizeCrawlerPathname(pathname);
    if (isWebsitePublicRedirectAliasPath(rawPathname)) return false;

    return WEBSITE_PRIVATE_CRAWLER_PATHS.some(
        (blockedPrefix) => rawPathname.startsWith(blockedPrefix) || normalizedPathname.startsWith(blockedPrefix),
    );
};

export const resolveDomainsRobotsDirectives = (requestUrl: string): string => {
    const url = new URL(requestUrl, 'https://unique.domains');
    const pathname = normalizeCrawlerPathname(url.pathname);

    if (pathname === '/domains' && url.search) {
        return FACETED_SEARCH_ROBOTS_DIRECTIVES;
    }

    return PUBLIC_INDEX_ROBOTS_DIRECTIVES;
};

/**
 * `/agenda` is indexable in one state only: the current month, no filter
 * (`isDefaultAgendaState` in the agenda contract, which the caller passes in). Every other
 * state is a facet of the same page (canonical `/agenda`), served `noindex, follow` so crawlers
 * keep walking its plain filter anchors without near-duplicate agendas entering the index.
 * Decided on the parsed state, which is safe because the page 301s any non-normalized query
 * first: a junk value never renders as the indexable default.
 *
 * Takes the state test's answer rather than the state: every landing page imports this
 * catalog, and the agenda codec (with the country table behind it) must ship with the
 * agenda page only.
 */
export const resolveAgendaRobotsDirectives = (isDefaultState: boolean): string =>
    isDefaultState ? PUBLIC_INDEX_ROBOTS_DIRECTIVES : FACETED_SEARCH_ROBOTS_DIRECTIVES;
